// src/sections/Dashboard.jsx — tenant analytics ("Report Attività", route /dashboard).
// Exports DashboardView({ tenantId, isSuperadmin, headerSlot }) — the KPI + charts body keyed on an
// EXPLICIT tenantId, also mounted by admin/pages/AdminOverviewPage for the superadmin "vedi come
// tenant" view — plus the default member wrapper (active tenant). Fed by 5 parallel /runs/stats
// (population-true) and 3 parallel /runs samples (limit=200); one failed call degrades its own
// charts, not the page.
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import { fetchRunsStats } from '../services/api/statsApi.js';
import { listRuns } from '../admin/runsApi.js';
import ChartCard from '../components/charts/ChartCard.jsx';
import BarTopFailureReasons from '../components/charts/BarTopFailureReasons.jsx';
import RunsPerDayLine    from '../components/charts/RunsPerDayLine.jsx';
import PlatformTotalsBar from '../components/charts/PlatformTotalsBar.jsx';
import ContentFormatBar  from '../components/charts/ContentFormatBar.jsx';
import PlatformByFormatBar from '../components/charts/PlatformByFormatBar.jsx';
import MockShareChip     from '../components/charts/MockShareChip.jsx';
import CriticScoreHistogram from '../components/charts/CriticScoreHistogram.jsx';
import IterationCountBar   from '../components/charts/IterationCountBar.jsx';
import WarningsHistogram   from '../components/charts/WarningsHistogram.jsx';
import DurationHistogram   from '../components/charts/DurationHistogram.jsx';
import ActivityHeatmap     from '../components/charts/ActivityHeatmap.jsx';
import { ArrowRight } from 'lucide-react';
import { FILTER_LABEL, FILTER_BUTTON, FilterDateField } from '../components/ui/filterFields.jsx';
import { PAGE_COLUMN } from '../components/ui/SectionCard.jsx';
import { getActiveLocale } from '../i18n/locale.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC, SHADOW } from '../constants/theme.js';
import PageBand from '../components/ui/PageBand.jsx';

const FONT = FONT_SANS;
const COLOR = { text: COLORS.ink, muted: COLORS.textSecondary, errFg: SEMANTIC.errore.fg, border: COLORS.border };

function formatItalianDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(getActiveLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
}

// Local-time YYYY-MM-DD (matches <input type="date"> and DateRangePicker output).
function toYmd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Default temporal filter: from one month ago to today (inclusive).
function defaultRange() {
  const now = new Date();
  const sinceDate = new Date(now);
  sinceDate.setMonth(sinceDate.getMonth() - 1);
  return { since: toYmd(sinceDate), before: toYmd(now) };
}

function KpiCard({ label, value }) {
  return (
    <div style={{
      flex: '0 0 auto', minWidth: 200,
      padding: '16px 18px', borderRadius: 8,
      border: `1px solid ${COLOR.border}`, background: COLORS.surface,
      fontFamily: FONT,
    }}>
      <div style={{ fontSize: 11, color: COLOR.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: COLOR.text, marginTop: 6 }}>{value}</div>
    </div>
  );
}

function SectionHeader({ title, hint }) {
  return (
    <div style={{ margin: '28px 0 12px' }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: COLOR.text, margin: 0 }}>{title}</h2>
      {hint && <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

/**
 * Tenant-scoped dashboard body (KPIs + charts) for an explicit `tenantId`.
 * Extracted from the member Dashboard so the superadmin overview can reuse it
 * to render ANY tenant's numbers ("vedi come tenant").
 *
 * - `tenantId`     drives every fetch; when falsy nothing is loaded and an
 *                  empty prompt is shown (SA hasn't picked a tenant yet).
 * - `isSuperadmin` gates the ops-only charts (error codes / warnings) — passed
 *                  as a prop, NOT read from useAuth, so the SA overview shows
 *                  them regardless of which tenant is selected.
 * - `headerSlot`   page header (title + greeting, or the SA tenant selector),
 *                  rendered by the caller and placed above the content.
 */
export function DashboardView({ tenantId, isSuperadmin = false, headerSlot = null }) {
  const { t } = useTranslation('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusData, setStatusData] = useState(null);    // RunStatsResponse | null
  const [dayData, setDayData] = useState(null);
  const [platformData, setPlatformData] = useState(null);
  const [contentFormatData, setContentFormatData] = useState(null);
  const [statusWithMockData, setStatusWithMockData] = useState(null);
  const [failedSample, setFailedSample] = useState(null);
  const [completedSample, setCompletedSample] = useState(null);
  const [allSample, setAllSample] = useState(null);
  const [range, setRange] = useState(() => defaultRange());
  const { since: sinceFilter, before: beforeFilter } = range;

  useEffect(() => {
    // No tenant selected → don't fire a tenant-less request; the empty prompt
    // below covers the "SA hasn't picked yet" case.
    if (!tenantId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');

    const dateOpts = { since: sinceFilter, before: beforeFilter };

    // Threading tenantId into every call keeps the URL tenant-keyed, which
    // matters because the BE's Cache-Control: private, max-age=60 caches by
    // URL only (X-Tenant-Id is NOT a cache key). Without this, a tenant
    // switch within 60s would surface the previous tenant's numbers.
    Promise.allSettled([
      fetchRunsStats({ groupBy: 'status',         tenant: tenantId, ...dateOpts }),
      fetchRunsStats({ groupBy: 'day',            tenant: tenantId, ...dateOpts }),
      fetchRunsStats({ groupBy: 'platform',       tenant: tenantId, ...dateOpts }),
      fetchRunsStats({ groupBy: 'content_format', tenant: tenantId, ...dateOpts }),
      fetchRunsStats({ groupBy: 'status', includeMock: true, tenant: tenantId, ...dateOpts }),
      listRuns({ tenant: tenantId, status: ['failed'],    limit: 200, ...dateOpts }),
      listRuns({ tenant: tenantId, status: ['completed'], limit: 200, ...dateOpts }),
      listRuns({ tenant: tenantId, limit: 200, ...dateOpts }),
    ]).then((results) => {
      if (cancelled) return;
      const [s, d, p, cf, sMock, failed, completed, all] = results;
      setStatusData(s.status     === 'fulfilled' ? s.value     : null);
      setDayData(d.status        === 'fulfilled' ? d.value     : null);
      setPlatformData(p.status   === 'fulfilled' ? p.value     : null);
      setContentFormatData(cf.status === 'fulfilled' ? cf.value : null);
      setStatusWithMockData(sMock.status === 'fulfilled' ? sMock.value : null);
      setFailedSample(failed.status === 'fulfilled' ? (failed.value?.runs || []) : null);
      setCompletedSample(completed.status === 'fulfilled' ? (completed.value?.runs || []) : null);
      setAllSample(all.status === 'fulfilled' ? (all.value?.runs || []) : null);

      const rejected = results.filter(r => r.status === 'rejected');
      if (rejected.length === results.length) {
        setError(rejected[0].reason?.message || t('errors.statsLoadFailed'));
      } else if (rejected.length > 0) {
        setError(t('errors.statsPartial'));
      }
      setLoading(false);
    });

    return () => { cancelled = true; };
    // `t` intentionally omitted: a language switch must not refetch stats.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, sinceFilter, beforeFilter]);

  const total      = statusData ? statusData.total : null;
  const since      = statusData?.since || dayData?.since || platformData?.since;
  const before     = statusData?.before || dayData?.before || platformData?.before;

  const fmt = (n) => (n == null ? '—' : String(n));

  return (
    // v193: the content sits in ONE rounded card in the centred 1280px column, like the other pages;
    // the filter row on top has "Storico Generazioni"'s look (components/ui/filterFields.jsx).
    <div style={PAGE_COLUMN}>
      {headerSlot}

      {!tenantId ? (
        <div style={{
          padding: '60px 40px', textAlign: 'center', background: COLORS.surface,
          borderRadius: RADII.section, border: `1px solid ${COLORS.border}`, boxShadow: SHADOW.card,
          color: COLOR.muted, fontSize: '13px',
        }}>
          {t('selectTenant')}
        </div>
      ) : (
        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          borderRadius: RADII.section, boxShadow: SHADOW.card, overflow: 'hidden',
        }}>
          {/* Filters */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '20px', flexWrap: 'wrap', padding: '24px' }}>
            <div>
              <div style={FILTER_LABEL}>{t('period')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FilterDateField
                  ariaLabel={t('rangeFrom')}
                  value={range.since}
                  onChange={(v) => setRange(r => ({ ...r, since: v }))}
                />
                <ArrowRight size={16} color={COLORS.textTertiary} aria-hidden="true" />
                <FilterDateField
                  ariaLabel={t('rangeTo')}
                  value={range.before}
                  onChange={(v) => setRange(r => ({ ...r, before: v }))}
                />
              </div>
            </div>
            <button type="button" onClick={() => setRange(defaultRange())} style={FILTER_BUTTON}>
              {t('lastMonth')}
            </button>
          </div>
          <div style={{ height: 1, background: COLORS.border }} />

          <div style={{ padding: '24px' }}>
            {error && (
              <div role="alert" style={{
                marginBottom: 16, padding: '8px 12px', borderRadius: 6,
                border: `1px solid ${COLOR.errFg}`, color: COLOR.errFg,
                background: SEMANTIC.errore.bg, fontSize: 12,
              }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <KpiCard label={t('kpi.total')} value={loading ? '…' : fmt(total)} />
            </div>

            <MockShareChip realOnly={statusData} withMock={statusWithMockData} />

            {/* ── SECTION 1: Utilizzo & adozione ── */}
            <SectionHeader
              title={t('sections.usage.title')}
              hint={t('sections.usage.hint')}
            />

            <ChartCard
              title={t('charts.dailyVolume.title')}
              subtitle={t('charts.dailyVolume.subtitle')}
              loading={loading}
              empty={!loading && (!dayData || (dayData.total || 0) === 0)}
              reliability={{ kind: 'exact' }}
            >
              <RunsPerDayLine data={dayData} />
            </ChartCard>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 280px', minWidth: 280 }}>
                <ChartCard
                  title={t('charts.platformVolume.title')}
                  subtitle={t('charts.platformVolume.subtitle')}
                  loading={loading}
                  empty={!loading && (!platformData || (platformData.total || 0) === 0)}
                  reliability={{ kind: 'exact' }}
                >
                  <PlatformTotalsBar data={platformData} />
                </ChartCard>
              </div>
              <div style={{ flex: '1 1 280px', minWidth: 280 }}>
                <ChartCard
                  title={t('charts.formatVolume.title')}
                  subtitle={t('charts.formatVolume.subtitle')}
                  loading={loading}
                  empty={!loading && (!contentFormatData || (contentFormatData.total || 0) === 0)}
                  reliability={{ kind: 'exact' }}
                >
                  <ContentFormatBar data={contentFormatData} />
                </ChartCard>
              </div>
            </div>

            <ChartCard
              title={t('charts.platformByFormat.title')}
              subtitle={t('charts.platformByFormat.subtitle')}
              loading={loading}
              empty={!loading && (!allSample || allSample.length === 0)}
              reliability={{ kind: 'sample', n: (allSample || []).length }}
            >
              <PlatformByFormatBar runs={allSample || []} />
            </ChartCard>

            <ChartCard
              title={t('charts.activity.title')}
              subtitle={t('charts.activity.subtitle')}
              loading={loading}
              empty={!loading && (!allSample || allSample.length === 0)}
              reliability={{ kind: 'sample', n: (allSample || []).length }}
            >
              <ActivityHeatmap runs={allSample || []} />
            </ChartCard>

            {/* ── SECTION 2: Salute della pipeline ──
                Distribuzione per stato e imbuto di completamento nascosti finché il
                flow di accettazione delle run non è definito. Codici d'errore e
                warning per run sono visibili solo ai superadmin. */}
            {isSuperadmin && (
              <>
                <SectionHeader
                  title={t('sections.health.title')}
                  hint={t('sections.health.hint')}
                />

                <ChartCard
                  title={t('charts.errorCodes.title')}
                  subtitle={t('charts.errorCodes.subtitle')}
                  loading={loading}
                  empty={!loading && (!failedSample || failedSample.length === 0)}
                  reliability={{ kind: 'sample', n: (failedSample || []).length }}
                >
                  <BarTopFailureReasons runs={failedSample || []} />
                </ChartCard>

                <ChartCard
                  title={t('charts.warnings.title')}
                  subtitle={t('charts.warnings.subtitle')}
                  loading={loading}
                  empty={!loading && (!completedSample || completedSample.length === 0)}
                  reliability={{ kind: 'sample', n: (completedSample || []).length }}
                >
                  <WarningsHistogram runs={completedSample || []} />
                </ChartCard>
              </>
            )}

            {/* ── SECTION 3: Qualità & efficienza ── */}
            <SectionHeader
              title={t('sections.quality.title')}
              hint={t('sections.quality.hint')}
            />

            <ChartCard
              title={t('charts.criticScore.title')}
              subtitle={t('charts.criticScore.subtitle')}
              loading={loading}
              empty={!loading && (!completedSample || completedSample.length === 0)}
              reliability={{ kind: 'sample', n: (completedSample || []).length }}
            >
              <CriticScoreHistogram runs={completedSample || []} />
            </ChartCard>

            <ChartCard
              title={t('charts.iterations.title')}
              subtitle={t('charts.iterations.subtitle')}
              loading={loading}
              empty={!loading && (!completedSample || completedSample.length === 0)}
              reliability={{ kind: 'sample', n: (completedSample || []).length }}
            >
              <IterationCountBar runs={completedSample || []} />
            </ChartCard>

            <ChartCard
              title={t('charts.duration.title')}
              subtitle={t('charts.duration.subtitle')}
              loading={loading}
              empty={!loading && (!completedSample || completedSample.length === 0)}
              reliability={{ kind: 'sample', n: (completedSample || []).length }}
            >
              <DurationHistogram runs={completedSample || []} />
            </ChartCard>

            {(since || before) && (
              <div style={{ fontSize: 11, color: COLOR.muted }}>
                {t('periodRange', { since: formatItalianDate(since), before: formatItalianDate(before) })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation('dashboard');
  const { t: tShell } = useTranslation('shell');
  const { user, activeTenant, isSuperadmin, adminMode } = useAuth();

  // SA in adminMode operates entirely under /admin/* — bounce before fetching.
  if (isSuperadmin && adminMode) return <Navigate to="/admin/users" replace />;

  // v193: the page header is the shared PageBand — portalled into the shell header's slot, on the
  // avatar's row, like the Content Generator — titled with the SIDEBAR's label ("Report Attività").
  // The superadmin overview keeps its own header (AdminOverviewPage → DashboardView's headerSlot).
  return (
    <>
      <PageBand
        title={tShell('nav.dashboard')}
        subtitle={`${t('welcome')}${user?.email ? `, ${user.email}` : ''}${activeTenant ? ` — ${activeTenant.display_name}` : ''}`}
      />
      <DashboardView
        tenantId={activeTenant?.tenant_id}
        isSuperadmin={isSuperadmin}
      />
    </>
  );
}
